import type { ReactEventHandler, SyntheticEvent } from 'react';
import React from 'react';
import type { ClassNameProp } from '../common-types';
import { showError, useObjectURL } from '../../lib';

export interface PDFJSPanelProps extends ClassNameProp {
  data: string | Blob;
  showToolbar?: boolean;
  showOpenFile?: boolean;
  onError?: ReactEventHandler<HTMLIFrameElement>;
  onLoad?: (e: SyntheticEvent) => void;
}

/**
 * Display a PDF in an inline pdf.js iframe.
 *
 * PDF.js is loaded from the WebResourceProviderHook, so this will not work in the storybook.
 *
 * TODO: support file url in addition to base64 data??
 * TODO: support easy customization as in the content viewer (hide header, etc.)?
 *
 *
 * @param data of the pdf
 * @param className optional css classes to set in addition to 'pdfjs-iframe'
 * @param onError error callback for exceptions in the iframe, defaults to {@link showError}
 * @param showToolbar if the pdfjs toolbar should be shown
 * @param showOpenFile if the open file button should be shown
 * @param onLoad callback that should be executed when iframe has loaded
 * @param props leftover props
 */
export function PDFJsIframe({
  data,
  showToolbar = false,
  showOpenFile = false,
  className,
  onError = showError,
  onLoad,
  ...props
}: Readonly<PDFJSPanelProps>): JSX.Element {
  const objectUrl = useObjectURL(data, 'application/pdf');

  let classes = 'ehs-pdfjs-iframe';
  if (className) classes += ' ' + className;

  return objectUrl ? (
    <iframe
      {...props}
      frameBorder="0"
      allowFullScreen
      className={classes}
      src={`/o/WebResourceProviderHook/pdfjs/web/viewer.html?file=${objectUrl}&showtoolbar=${showToolbar}&showopenfile=${showOpenFile}`}
      onError={onError}
      onLoad={onLoad}
    />
  ) : (
    <></>
  );
}
